import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuthStore } from '../../../stores/authStore';

export interface ServicePricingSummary {
  id: string;
  name: string;
  price: number;
  duration_minutes: number;
  enable_dynamic_pricing: boolean;
  min_price: number | null;
  max_price: number | null;
  intro_enabled: boolean;
  intro_price: number | null;
  intro_label: string | null;
}

export function useServicesPricing() {
  const business = useAuthStore(s => s.business);
  const businessId = business?.id;
  const mountedRef = useRef(true);

  const [services, setServices] = useState<ServicePricingSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [dynamicPricingEnabled, setDynamicPricingEnabled] = useState(false);
  const [bookingSlug, setBookingSlug] = useState<string | null>(null);

  const tier = business?.tier ?? 'free';

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!businessId) { setLoading(false); return; }

    (async () => {
      setLoading(true);
      try {
        const [svcRes, bizRes, bpRes] = await Promise.all([
          supabase
            .from('services')
            .select('id, name, price, duration_minutes, enable_dynamic_pricing, min_price, max_price, intro_enabled, intro_price, intro_label')
            .eq('business_id', businessId)
            .eq('is_active', true)
            .order('sort_order')
            .order('name'),
          supabase
            .from('businesses')
            .select('enable_dynamic_pricing')
            .eq('id', businessId)
            .maybeSingle(),
          supabase
            .from('booking_pages')
            .select('slug')
            .eq('business_id', businessId)
            .maybeSingle(),
        ]);

        if (!mountedRef.current) return;
        setServices((svcRes.data ?? []) as ServicePricingSummary[]);
        setDynamicPricingEnabled((bizRes.data as Record<string, unknown>)?.enable_dynamic_pricing === true);
        setBookingSlug(bpRes.data?.slug ?? null);
      } catch { /* silent */ }
      finally { if (mountedRef.current) setLoading(false); }
    })();
  }, [businessId]);

  const toggleDynamicPricing = useCallback(async () => {
    if (!businessId) return;
    const newVal = !dynamicPricingEnabled;
    setDynamicPricingEnabled(newVal);
    try {
      await supabase.from('businesses').update({ enable_dynamic_pricing: newVal }).eq('id', businessId);
    } catch {
      if (mountedRef.current) setDynamicPricingEnabled(!newVal);
    }
  }, [businessId, dynamicPricingEnabled]);

  const previewService = services.find(s => s.enable_dynamic_pricing && s.min_price != null) ?? null;
  const introPreviewService = services.find(s => s.intro_enabled && s.intro_price != null) ?? null;

  return {
    dynamicPricingEnabled,
    toggleDynamicPricing,
    tier,
    services,
    loading,
    previewService,
    introPreviewService,
    bookingSlug,
  };
}
